/**
 * Timers for the replayed call and the fixture audio (admin mocks, tests). Chrome throttles a page's own timers while
 * the page is hidden (another Space, minimized, covered): measured on the deployed app, a 40 ms interval fired 10
 * times in 10 s instead of 250, which stalled walkthroughs. A dedicated worker's timers aren't throttled (250 of 250),
 * so these run on one. Without workers (unit tests in Node), or once the worker fails, they use the page's timers.
 */
const WORKER = `
const timers = new Map();
onmessage = ({ data }) => {
  if (data.stop) {
    clearInterval(timers.get(data.id));
    timers.delete(data.id);
  } else if (data.repeat) {
    timers.set(data.id, setInterval(() => postMessage(data.id), data.ms));
  } else {
    timers.set(data.id, setTimeout(() => { timers.delete(data.id); postMessage(data.id); }, data.ms));
  }
};`;

type Timer = { ms: number; repeat: boolean; fn: () => void; page?: ReturnType<typeof setTimeout> };
const timers = new Map<number, Timer>();
let nextId = 1;
let worker: Worker | null | undefined;

function onPage(t: Timer) {
  t.page = t.repeat ? setInterval(t.fn, t.ms) : setTimeout(t.fn, t.ms);
}

function clockWorker(): Worker | null {
  if (worker !== undefined) return worker;
  try {
    worker =
      typeof Worker === 'undefined' || typeof Blob === 'undefined' || typeof URL.createObjectURL !== 'function'
        ? null
        : new Worker(URL.createObjectURL(new Blob([WORKER], { type: 'text/javascript' })));
  } catch {
    worker = null;
  }
  worker?.addEventListener('message', (e: MessageEvent<number>) => timers.get(e.data)?.fn());
  // A blocked blob worker (a CSP without blob:) can fail after construction: move to the page's timers, and re-arm
  // the ones the worker held, so a replay or clip never waits on a dead worker.
  worker?.addEventListener('error', () => {
    console.warn('[fuse] clock worker failed; mocks fall back to page timers');
    worker?.terminate();
    worker = null;
    for (const t of timers.values()) if (t.page === undefined) onPage(t);
  });
  return worker;
}

function start(t: Timer): number {
  const id = nextId++;
  timers.set(id, t);
  const w = clockWorker();
  if (w) w.postMessage({ id, ms: t.ms, repeat: t.repeat });
  else onPage(t);
  return id;
}

function cancel(id: number) {
  const t = timers.get(id);
  if (!t) return;
  timers.delete(id);
  if (t.page !== undefined) clearInterval(t.page);
  else worker?.postMessage({ id, stop: true });
}

/** Calls `fn` every `ms` until the returned stop function runs. */
export function every(ms: number, fn: () => void): () => void {
  const id = start({ ms, repeat: true, fn });
  return () => cancel(id);
}

export function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => {
    const id = start({
      ms,
      repeat: false,
      fn: () => {
        cancel(id);
        resolve();
      },
    });
  });
}
